/**
 * Toast wrapper with sensible default durations per type.
 *
 * - success: 2s
 * - info:    3s
 * - error:   4.5s
 * - warning: 4s
 * - default: 3s
 *
 * Usage (drop-in replacement for sonner):
 *   import { toast } from "@/lib/toast";
 *   toast.success("Enregistre");
 *   toast.error("Erreur reseau");        // 4.5s
 *   toast("Hello");                      // neutral, 3s
 *   toast.success("fast", { duration: 1000 });  // override supported
 */
import { toast as sonnerToast } from "sonner";

const DURATIONS = {
  success: 2000,
  info: 3000,
  error: 4500,
  warning: 4000,
  message: 3000,
  loading: Infinity,
};

function withDuration(type, fn) {
  return (message, options = {}) => {
    const duration = DURATIONS[type] ?? 3000;
    return fn(message, { duration, ...options });
  };
}

// Create a callable proxy that preserves default sonnerToast() behaviour
function baseToast(message, options = {}) {
  return sonnerToast(message, { duration: 3000, ...options });
}

baseToast.success = withDuration("success", sonnerToast.success);
baseToast.error = withDuration("error", sonnerToast.error);
baseToast.info = withDuration("info", sonnerToast.info);
baseToast.warning = withDuration("warning", sonnerToast.warning);
baseToast.message = withDuration("message", sonnerToast.message);
baseToast.loading = sonnerToast.loading; // Infinity by default, explicit dismiss

// Preserve other sonner helpers (dismiss, promise, custom, etc.)
baseToast.dismiss = sonnerToast.dismiss;
baseToast.promise = sonnerToast.promise;
baseToast.custom = sonnerToast.custom;

export const toast = baseToast;
